import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { supabaseClient } from '../../services/supabase';

interface CumulativeSolarCapacityChartProps {
  location: string;
  geofips: number;
}

interface SolarData {
  year: number;
  cumulativeCapacity: number;
  annualCapacity: number;
  sectors: { [sector: string]: number };
}

export const CumulativeSolarCapacityChart: React.FC<CumulativeSolarCapacityChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSolarData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch solar data for the location
        const { data: locationData, error: locationError } = await supabaseClient
          .from('all_pv_size')
          .select('year, sector, total_watts')
          .eq('geoid', geofips)
          .order('year', { ascending: true });

        if (locationError) {
          console.error('Error fetching solar data:', locationError);
          throw locationError;
        }

        // Process data to calculate cumulative capacity by sector
        const yearSectorMap = new Map<number, Map<string, number>>();
        locationData?.forEach(record => {
          const year = parseInt(record.year.toString());
          if (year > 2024) return; // Filter out future years
          
          if (!yearSectorMap.has(year)) {
            yearSectorMap.set(year, new Map());
          }
          const sectorMap = yearSectorMap.get(year)!;
          const sector = record.sector || 'unknown';
          const capacity = (parseFloat(record.total_watts.toString()) || 0) / 1000;
          sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacity);
        });

        // Get all sectors
        const allSectors = new Set<string>();
        yearSectorMap.forEach(sectorMap => {
          sectorMap.forEach((_, sector) => allSectors.add(sector));
        });

        // Convert to cumulative data
        const sortedYears = Array.from(yearSectorMap.keys()).sort();
        const sectorCumulativeMap = new Map<string, number>();
        allSectors.forEach(sector => sectorCumulativeMap.set(sector, 0));

        const processedData: SolarData[] = sortedYears.map(year => {
          const yearSectorMap_data = yearSectorMap.get(year) || new Map();
          const sectors: { [sector: string]: number } = {};
          let annualCapacity = 0;

          allSectors.forEach(sector => {
            const sectorCapacity = yearSectorMap_data.get(sector) || 0;
            const cumulativeSectorCapacity = sectorCumulativeMap.get(sector)! + sectorCapacity;
            sectorCumulativeMap.set(sector, cumulativeSectorCapacity);
            sectors[sector] = cumulativeSectorCapacity;
            annualCapacity += sectorCapacity;
          });

          const cumulativeCapacity = Array.from(sectorCumulativeMap.values()).reduce((sum, val) => sum + val, 0);

          return {
            year,
            cumulativeCapacity,
            annualCapacity,
            sectors
          };
        });

        // Create chart
        if (chartRef.current && processedData.length > 0) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: processedData.map(d => d.year.toString()),
                datasets: Array.from(allSectors).map((sector, index) => {
                  const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#06b6d4'];
                  const color = colors[index % colors.length];
                  
                  return {
                    label: sector.charAt(0).toUpperCase() + sector.slice(1),
                    data: processedData.map(d => d.sectors[sector] || 0),
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1
                  };
                })
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: `Cumulative Solar Capacity by Sector - ${location}`,
                    font: { size: 16, weight: 'bold' }
                  },
                  legend: {
                    position: 'top',
                    labels: {
                      usePointStyle: true,
                      padding: 20
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y;
                        return `${label}: ${value.toFixed(1)} kW`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    stacked: true,
                    title: {
                      display: true,
                      text: 'Year',
                      font: { size: 14, weight: 'bold' }
                    }
                  },
                  y: {
                    stacked: true,
                    title: {
                      display: true,
                      text: 'Cumulative Solar Capacity (kW)',
                      font: { size: 14, weight: 'bold' }
                    },
                    beginAtZero: true,
                    ticks: {
                      callback: (value: any) => `${value.toLocaleString()} kW`
                    }
                  }
                }
              }
            });
          }
        }

      } catch (err) {
        console.error('Error fetching cumulative solar data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load cumulative solar data');
      } finally {
        setLoading(false);
      }
    };

    fetchSolarData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading cumulative solar capacity data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ height: '380px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};
